'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Pixel offset and direction the element travels in from. Default: 'up' (12px). */
  direction?: Direction;
  /** Animation duration in ms. */
  duration?: number;
  /** Delay before animation starts in ms. */
  delay?: number;
  /** IntersectionObserver threshold (0–1). */
  threshold?: number;
  /** Run the reveal only once. */
  once?: boolean;
  /** Render as a different tag than div. */
  as?: keyof JSX.IntrinsicElements;
}

const offsetFor: Record<Direction, string> = {
  up: 'translate3d(0, 16px, 0)',
  down: 'translate3d(0, -16px, 0)',
  left: 'translate3d(16px, 0, 0)',
  right: 'translate3d(-16px, 0, 0)',
  none: 'none',
};

export function Reveal({
  children,
  className,
  direction = 'up',
  duration = 700,
  delay = 0,
  threshold = 0.12,
  once = true,
  as: Tag = 'div',
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  const style: React.CSSProperties = {
    transitionProperty: 'transform, opacity',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    transitionDelay: `${delay}ms`,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0,0,0)' : offsetFor[direction],
    willChange: 'transform, opacity',
  };

  return (
    // @ts-expect-error — generic JSX tag.
    <Tag ref={ref} style={style} className={cn(className)}>
      {children}
    </Tag>
  );
}
